// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Taking a ramp or a habitrail off the machine (M9-04 follow-up, 11 §3.1).
//
// The Builder could reshape a ramp and take control points out of it, and could not take the ramp
// itself away — so an author remixing a machine with a ramp they did not want had no way to lose
// it short of editing JSON. This is the rule for doing that safely, shared by the web Builder and
// the MCP server's `edit_ramp` so the two cannot disagree about what a removal leaves behind.
//
// **What belongs to the ramp goes with it.** A ramp is declared as a layer and becomes a device at
// load (`validate.ts`: "a ramp could not have a switch" otherwise), so the switches that name it as
// their device and the support posts — colliders whose `device` is the ramp — are its wiring and
// its legs. Leaving them behind would leave records naming something that is not there, which the
// validator refuses, with the fault in a record the author never touched. This is the same reason
// `layoutDraft.remove` takes a device's switches and coils with it.
//
// **What leads onto it or stands on it is refused, by name.** Another ramp delivering onto this
// habitrail, a scoop or tunnel whose exit is this rail, a wall or a part standing on this level:
// those are somebody else's records, and quietly rewriting where another ramp delivers would be a
// second edit the author did not ask for. The refusal says which ones, so the order is obvious —
// take the ramp off before the rail it feeds.
//
// **Rules are left to the validator.** A rule the author wrote that names the ramp's switch is
// their rule; the overlay says so the moment it stops making sense, exactly as it does for a device.
//
// Pure: no DOM, no clock.

import type { MachineDefinition } from '../../core/ts/machine'

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {}
}

function listOf(definition: MachineDefinition, key: string): readonly unknown[] {
  const list = asRecord(definition)[key]
  return Array.isArray(list) ? list : []
}

function idOf(entry: unknown): string {
  const id = asRecord(entry)['id']
  return typeof id === 'string' ? id : '?'
}

function exitsTo(entry: unknown, id: string): boolean {
  return asRecord(asRecord(entry)['exit'])['to'] === id
}

/**
 * Everything that leads onto, or stands on, one layer — in the words the refusal uses.
 *
 * Another layer entering from it or exiting onto it; a collider, device or toy on it; a device or
 * toy whose exit delivers to it.
 */
export function standingOn(definition: MachineDefinition, id: string): readonly string[] {
  const named: string[] = []
  for (const other of listOf(definition, 'layers')) {
    if (idOf(other) === id) continue
    if (asRecord(asRecord(other)['entry'])['from'] === id || exitsTo(other, id)) named.push(`layer ${idOf(other)}`)
  }
  for (const wall of listOf(definition, 'colliders')) {
    if (asRecord(wall)['layer'] === id) named.push(`collider ${idOf(wall)}`)
  }
  for (const key of ['devices', 'toys'] as const) {
    const label = key === 'devices' ? 'device' : 'toy'
    for (const one of listOf(definition, key)) {
      if (asRecord(one)['layer'] === id || exitsTo(one, id)) named.push(`${label} ${idOf(one)}`)
    }
  }
  return named
}

/** Why a ramp or a habitrail may not be removed, or nothing when it may. */
export function layerRemovalRefusal(definition: MachineDefinition, id: string): string | undefined {
  const named = standingOn(definition, id)
  if (named.length === 0) return undefined
  const one = named.length === 1
  const shown = named.slice(0, 6).join(', ')
  const more = named.length > 6 ? `, and ${named.length - 6} more` : ''
  return `${named.length} thing${one ? '' : 's'} lead${one ? 's' : ''} onto or stand${one ? 's' : ''} on ${id} — ${shown}${more}. Move or remove ${one ? 'it' : 'them'} first.`
}

/** A layer removed, and what went with it. */
export interface LayerRemoval {
  readonly definition: MachineDefinition
  /** The ids of the switches and support colliders that belonged to it, in that order. */
  readonly alsoRemoved: readonly string[]
}

/**
 * The machine without one layer, its switches and its supports.
 *
 * Does not refuse: call `layerRemovalRefusal` first. Returns the definition unchanged when there is
 * no such layer.
 */
export function withoutLayer(definition: MachineDefinition, id: string): LayerRemoval {
  const layers = listOf(definition, 'layers')
  if (!layers.some((layer) => idOf(layer) === id)) return { definition, alsoRemoved: [] }
  const belongs = (entry: unknown): boolean => asRecord(entry)['device'] === id
  const switches = listOf(definition, 'switches')
  const colliders = listOf(definition, 'colliders')
  const alsoRemoved = [...switches.filter(belongs).map(idOf), ...colliders.filter(belongs).map(idOf)]
  const record = asRecord(definition)
  const next = {
    ...record,
    layers: layers.filter((layer) => idOf(layer) !== id),
    colliders: colliders.filter((entry) => !belongs(entry)),
    ...(Array.isArray(record['switches']) ? { switches: switches.filter((entry) => !belongs(entry)) } : {}),
  } as unknown as MachineDefinition
  return { definition: next, alsoRemoved }
}
